import { Box, Button, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDropzone } from "react-dropzone";
// type
import { LoadingButton } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import { DialogComponent } from "@components/Dialog";
import useLocales from "../../hooks/useLocales";
import BlockContent from "./BlockContent";
import MultiFilePreview from "./MultiFilePreview";
import RejectionFiles from "./RejectionFiles";
import { UploadMultiFileProps } from "./type";

// ----------------------------------------------------------------------

const DropZoneStyle = styled("div")(({ theme }) => ({
  outline: "none",
  // padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  "&:hover": { opacity: 0.72, cursor: "pointer" },
}));

// ----------------------------------------------------------------------

export default function UploadMultiFile({
  loading,
  error,
  validator,
  showPreview = false,
  files,
  onUpload,
  onRemove,
  onRemoveAll,
  helperText,
  sx,
  handleCloseModal,
  ...other
}: UploadMultiFileProps) {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
    acceptedFiles,
  } = useDropzone({
    ...other,
  });
  const { translate } = useLocales();
  const theme = useTheme();

  const fileMessage = `a_maximum_of_10_files_are_allowed_in_a_single_upload`;

  return (
    <DialogComponent
      isShowModal={true}
      showModalMethod={handleCloseModal!}
      fullWidth
      maxWidth="md"
      dialogTitle={translate("upload_by_device")}
      dialogContent={
        <Box sx={{ pt: 1, pb: 1 }}>
          <DropZoneStyle
            {...getRootProps()}
            sx={{
              ...(isDragActive && { opacity: 0.72 }),
              ...((isDragReject ?? error) && {
                color: "error.main",
                borderColor: "error.light",
                bgcolor: "error.lighter",
              }),
              border: "none",
            }}
          >
            <input {...getInputProps()} />
            <BlockContent fileName={undefined} fileMessage={fileMessage} />
          </DropZoneStyle>

          {fileRejections.length > 0 && (
            <RejectionFiles fileRejections={fileRejections} />
          )}

          <MultiFilePreview
            files={files}
            showPreview={showPreview}
            onRemove={onRemove}
            loading={false}
          />

          {files.length > 0 && (
            <Typography variant="subtitle1" sx={{ ml: 2, mt: 2 }}>
              {translate(`finance_screen.selected_files`)}: {files?.length}
            </Typography>
          )}

          {helperText && helperText}
        </Box>
      }
      dialogActions={
        files.length > 0 ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" variant="outlined" onClick={onRemoveAll}>
              {translate("Cancel")}
            </Button>
            <LoadingButton
              loading={loading}
              loadingPosition="start"
              size="small"
              variant="contained"
              onClick={onUpload}
            >
              {translate("upload")}
            </LoadingButton>
          </Box>
        ) : undefined
      }
    />
  );
}
